import React, { useMemo, useState } from 'react';
import { useMutation } from 'urql';

import { Button, LoginGateBlock, QtyField, ShareBlock } from '@/components';
import { useIsMobile, useStore } from '@/hooks';
import { trackEvent, trackProduct } from '@/lib/GA';
import { extractItems } from '@/lib/GraphQLHelper';
import { currencyFormat } from '@/lib/Utils';

import HeartFilledIcon from '@/images/icons/heart--filled.svg';
import HeartIcon from '@/images/icons/heart.svg';

import mutation from './mutation.graphql';

import styles from './styles.module.scss';

interface Props {
  product: ETicketFragment;
  inWishlist?: boolean;
}

const AddToCartBlock = ({ product, inWishlist }: Props) => {
  const { addToCraftCommerce } = useStore('cart');
  const { loggedIn } = useStore('userStore');
  const [isMobile] = useIsMobile(768);

  const [qty, setQty] = useState(1);
  const [maxLimit, setMax] = useState(1);
  const [unlimited, setUnlimited] = useState(false);
  const [goToCart, setGoToCart] = useState(false);
  const [, mutate] = useMutation(mutation);
  const [addedToWishlist, setAddedToWishlist] = useState(inWishlist || false);

  const handleAddToWishlist = () => {
    setAddedToWishlist(!addedToWishlist);
    mutate({ id: product.id });
    trackEvent(
      'Wishlist',
      `Clicked ${!addedToWishlist ? 'add to wish list' : 'remove from wishlist'}`,
      `${product.title}`,
    );
  };

  const productCategories = extractItems<ProductCategoryFragment>(product?.productCategories);

  const soldOut = useMemo(() => {
    const productVariants = extractItems<ETicketsVariant>(product?.variants);

    // if hasUnlimitedStock it will never be sold out
    if (productVariants?.[0]?.hasUnlimitedStock) {
      setUnlimited(true);
      return false;
    }

    // set the max amount of stock available & let user know it's available
    if (productVariants?.[0]?.stock) {
      setMax(productVariants[0].stock);
      return productVariants[0].stock <= 0;
    }

    // shouldn't happen but don't let user buy it if it gets here
    return true;
  }, [product?.variants]);

  const handleAddToCart = async (e: React.FormEvent) => {
    e.preventDefault();
    // don't trust input limits, make sure they can't order
    if (!unlimited && qty > maxLimit) {
      return;
    }

    if (!soldOut && loggedIn) {
      await addToCraftCommerce(product, qty);
      setGoToCart(true);
    }

    trackProduct(
      {
        id: product.id!,
        name: product.title!,
        category: productCategories?.[0]?.title || '',
        price: product.defaultPrice!,
        quantity: qty,
      },
      'addToCart',
    );
  };

  const buyitNow = async () => {
    if (!unlimited && qty > maxLimit) {
      return;
    }

    if (!soldOut && loggedIn) {
      await addToCraftCommerce(product, qty);
      window.location.assign('/cart');
    }
  };

  if (!loggedIn) {
    return <LoginGateBlock>You must be a member to purchase this product</LoginGateBlock>;
  }

  if (soldOut) {
    return (
      <div className={styles.addToCartBlock}>
        <div className={styles.soldOut}>Sold Out</div>
      </div>
    );
  }

  return (
    <div>
      {isMobile ? (
        <>
          <div className={styles.mobilePrices}>
            <div className={styles.prices}>
              <h3>{currencyFormat(product?.defaultPrice)}</h3>
              <span>{currencyFormat(product?.rrp)}</span>
              {product.rrp && product.defaultPrice && (
                <div className={styles.discount}>
                  Save <strong>{Math.floor((1 - product.defaultPrice / product.rrp) * 100)}%</strong>
                </div>
              )}
            </div>
            <div className={styles.smallWishlist}>
              <Button
                design="highlight-borders"
                size="large"
                data-wishlist={addedToWishlist}
                data-testid="wishlist-btn"
                onClick={handleAddToWishlist}
              >
                {addedToWishlist ? <HeartFilledIcon /> : <HeartIcon />}
              </Button>
            </div>
          </div>
          <div className={styles.mobileBlock}>
            <Button design="highlight-borders" size="large" as="submit">
              Add to cart
            </Button>
            {goToCart ? (
              <Button design="pricesavings" size="large" href="/cart">
                View cart
              </Button>
            ) : (
              <Button design="highlight" size="large" onClick={buyitNow}>
                Buy now
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className={styles.addToCartBlock}>
            <h3>
              {currencyFormat(product?.defaultPrice)}
              &nbsp;
              <span>{currencyFormat(product?.rrp)}</span>
            </h3>
            {product.rrp && product.defaultPrice && (
              <div className={styles.discount}>
                Save <strong>{Math.floor((1 - product.defaultPrice / product.rrp) * 100)}%</strong>
              </div>
            )}
            <div>
              {!unlimited && maxLimit <= 10 && <div className={styles.limitWarning}>Only {maxLimit} left!</div>}
              <div className={styles.cart}>
                <form onSubmit={handleAddToCart}>
                  <QtyField onChange={setQty} max={(!unlimited && maxLimit) || undefined} />
                  <Button design="highlight-borders" size="large" as="submit">
                    Add to cart
                  </Button>
                </form>
              </div>
              <div className={styles.buttons}>
                {goToCart ? (
                  <Button design="pricesavings" size="large" href="/cart">
                    View cart
                  </Button>
                ) : (
                  <Button design="highlight" size="large" onClick={buyitNow}>
                    Buy now
                  </Button>
                )}
                <Button
                  design="highlight-borders"
                  size="large"
                  data-wishlist={addedToWishlist}
                  data-testid="wishlist-btn"
                  onClick={handleAddToWishlist}
                >
                  {addedToWishlist ? 'Remove from' : 'Add to'} wishlist
                </Button>
              </div>
            </div>
          </div>
          <ShareBlock />
        </>
      )}
    </div>
  );
};

export default AddToCartBlock;
